import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Button,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  useToast,
  Switch,
  Divider,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiRefreshCw,
  FiAlertTriangle,
  FiSlash,
  FiHeart,
  FiLogOut,
  FiActivity,
} from 'react-icons/fi';
import {
  listStudentStatuses,
  upsertStudentStatus,
  deleteStudentStatus,
  getStudentsCache,
} from 'services/qrService';
import StudentPicker from './StudentPicker';
import StaffPicker from 'components/Search/StaffPicker';
import GuardianPicker from 'components/Search/GuardianPicker';

const TYPE_CONFIG = {
  suspended:        { label: 'Suspendido',       color: 'red',    icon: FiSlash },
  medical_leave:    { label: 'Licencia Médica',  color: 'orange', icon: FiHeart },
  pedagogical_exit: { label: 'Salida Pedagógica', color: 'purple', icon: FiLogOut },
  sick_exit:        { label: 'Salida por Enfermedad', color: 'teal', icon: FiActivity },
};

const SICK_EXIT_TITLES = ['Accidente escolar', 'Enfermedad', 'Otros'];

const EMPTY_FORM = {
  id: null,
  student: null, // { id, username, name }
  type: 'suspended',
  reason: '',
  notes: '',
  starts_at: '',
  ends_at: '',
  date: '',                // solo aplica a type "sick_exit" (una sola fecha, sin rango)
  authorized_by: null,     // persona elegida vía StaffPicker
  responsible_name: null,  // idem — aplica a "pedagogical_exit" y "sick_exit"
  active: true,
};

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toDateOnly = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const StudentStatusesTab = () => {
  const [statuses, setStatuses] = useState([]);
  const [namesByRut, setNamesByRut] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { isOpen: isFormOpen, onOpen: onOpenForm, onClose: onCloseForm } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const rowHover = useColorModeValue('gray.50', 'gray.700');

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStudentStatuses();
      setStatuses(res.data ?? []);
    } catch {
      setError('No se pudo cargar la lista de estados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  // Carga liviana de nombres para mostrar en la tabla (cache de alumnos con token QR)
  useEffect(() => {
    getStudentsCache(1, 200)
      .then(res => {
        const map = {};
        (res.data ?? []).forEach(s => { map[s.rut] = s.nombre; });
        setNamesByRut(map);
      })
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    onOpenForm();
  };

  const openEdit = (status) => {
    setForm({
      id: status.id,
      student: {
        id: status.wp_user_id,
        username: status.rut,
        name: namesByRut[status.rut] ?? status.rut,
      },
      type: status.type,
      reason: status.reason ?? '',
      notes: status.notes ?? '',
      starts_at: toDatetimeLocal(status.starts_at),
      ends_at: toDatetimeLocal(status.ends_at),
      date: toDateOnly(status.starts_at),
      // Solo se guardó el nombre (string) — se sintetiza un objeto mínimo
      // para que el StaffPicker lo muestre como "seleccionado"; "Cambiar"
      // permite reemplazarlo por un usuario real buscado.
      authorized_by: status.authorized_by ? { name: status.authorized_by } : null,
      responsible_name: status.responsible_name ? { name: status.responsible_name } : null,
      active: status.active,
    });
    setEditing(status.id);
    onOpenForm();
  };

  const handleSave = async () => {
    if (!form.student) {
      toast({ title: 'Selecciona un alumno', status: 'warning', duration: 2500 });
      return;
    }
    if (form.type === 'sick_exit' && !form.date) {
      toast({ title: 'Selecciona la fecha', status: 'warning', duration: 2500 });
      return;
    }
    setSaving(true);
    try {
      const isSickExit = form.type === 'sick_exit';
      const res = await upsertStudentStatus({
        id: form.id,
        wp_user_id: form.student.id,
        rut: form.student.username,
        type: form.type,
        reason: form.reason.trim() || null,
        notes: form.notes.trim() || null,
        starts_at: isSickExit ? `${form.date}T00:00:00` : (form.starts_at || null),
        ends_at: isSickExit ? `${form.date}T23:59:59` : (form.ends_at || null),
        authorized_by: form.authorized_by?.name?.trim() || null,
        responsible_name: ['pedagogical_exit', 'sick_exit'].includes(form.type) ? (form.responsible_name?.name?.trim() || null) : null,
        active: form.active,
      });
      toast({ title: editing ? 'Estado actualizado' : 'Estado creado', status: 'success', duration: 2500 });
      const deactivated = res?.data?.deactivated ?? [];
      if (deactivated.length > 0) {
        const labels = deactivated.map(s => typeOf(s.type).label).join(', ');
        toast({
          title: 'Estado anterior desactivado',
          description: `Se desactivó automáticamente: ${labels} (un alumno solo puede tener un estado vigente a la vez).`,
          status: 'info',
          duration: 5000,
        });
      }
      onCloseForm();
      fetchStatuses();
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (status) => {
    setDeleteTarget(status);
    onOpenDelete();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.id);
    try {
      await deleteStudentStatus(deleteTarget.id);
      toast({ title: 'Estado eliminado', status: 'success', duration: 2500 });
      onCloseDelete();
      fetchStatuses();
    } catch {
      toast({ title: 'Error al eliminar', status: 'error', duration: 3000 });
    } finally {
      setDeleting(null);
    }
  };

  const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const typeOf = (type) => TYPE_CONFIG[type] ?? TYPE_CONFIG.suspended;

  return (
    <Box fontFamily="jakarta">
      <Flex justify="space-between" align="center" mb={4} gap="10px" flexWrap="wrap">
        <Box>
          <Text fontSize="18px" fontWeight="800" color="ink.900" letterSpacing="-0.02em">Estados de Alumnos</Text>
          <Text fontSize="13px" color="ink.400" fontWeight="500" mt="2px">
            {statuses.length} estado{statuses.length !== 1 ? 's' : ''} registrado{statuses.length !== 1 ? 's' : ''} · {statuses.filter(s => s.active).length} activo{statuses.filter(s => s.active).length !== 1 ? 's' : ''}
          </Text>
        </Box>
        <HStack spacing="8px">
          <Tooltip label="Refrescar">
            <IconButton
              icon={<FiRefreshCw />}
              h="38px" w="38px" borderRadius="11px"
              border="1.5px solid" borderColor="cream.line2"
              bg="white" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              onClick={fetchStatuses} isLoading={loading} aria-label="Refrescar"
            />
          </Tooltip>
          <Button
            h="38px" px={4} borderRadius="11px"
            bg="coral.500" color="#fff" fontWeight="700" fontSize="13px"
            _hover={{ bg: 'coral.600' }}
            boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
            leftIcon={<FiPlus />}
            onClick={openCreate}
          >
            Nuevo estado
          </Button>
        </HStack>
      </Flex>

      <Box bg="white" borderRadius="22px" overflow="hidden" border="1px solid" borderColor="cream.line"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)">
        {error && (
          <Alert status="error" m={4} borderRadius="10px">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <VStack py={12} spacing={3}>
            <Spinner size="lg" color="primary.500" />
            <Text fontSize="sm" color={mutedColor}>Cargando estados…</Text>
          </VStack>
        ) : statuses.length === 0 ? (
          <VStack py={12} spacing={2}>
            <Icon as={FiAlertTriangle} color={mutedColor} boxSize={8} />
            <Text color={mutedColor} fontSize="sm">Sin estados registrados</Text>
          </VStack>
        ) : (
          <TableContainer overflowX="auto">
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr bg="cream.bg2" borderBottom="1px solid" borderColor="cream.line">
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Alumno</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Tipo</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', md: 'table-cell' }}>Motivo</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', lg: 'table-cell' }}>Vigencia</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', sm: 'table-cell' }}>Estado</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {statuses.map(status => {
                  const cfg = typeOf(status.type);
                  const typeColors = {
                    suspended:        { bg: '#FEE2E2', color: '#DC2626' },
                    medical_leave:    { bg: '#FEF3C7', color: '#D97706' },
                    pedagogical_exit: { bg: '#EDE9F8', color: '#6D28D9' },
                    sick_exit:        { bg: '#CCFBF1', color: '#0F766E' },
                  };
                  const tc = typeColors[status.type] ?? typeColors.suspended;
                  return (
                    <Tr key={status.id}
                      _hover={{ bg: 'coral.tint' }} transition="background .13s"
                      opacity={status.active ? 1 : 0.5}
                      borderTop="1px solid" borderColor="cream.line"
                    >
                      <Td py={3} maxW="180px">
                        <Text fontWeight="700" fontSize="14px" color="ink.900" noOfLines={1}>
                          {namesByRut[status.rut] ?? status.rut}
                        </Text>
                        <Text fontSize="11.5px" color="ink.400" fontWeight="500">RUT {status.rut}</Text>
                      </Td>
                      <Td py={3}>
                        <HStack
                          as="span" display="inline-flex" align="center" spacing={1}
                          px="10px" py="4px" borderRadius="999px"
                          bg={tc.bg} color={tc.color}
                          fontSize="11px" fontWeight="800"
                        >
                          <Icon as={cfg.icon} boxSize={3} />
                          <Text>{cfg.label}</Text>
                        </HStack>
                      </Td>
                      <Td py={3} fontSize="13.5px" color="ink.700" fontWeight="500" display={{ base: 'none', md: 'table-cell' }} maxW="200px">
                        <Text noOfLines={1}>{status.reason || '—'}</Text>
                      </Td>
                      <Td py={3} fontSize="13px" color="ink.400" display={{ base: 'none', lg: 'table-cell' }}>
                        {formatDate(status.starts_at)} → {formatDate(status.ends_at)}
                      </Td>
                      <Td py={3} display={{ base: 'none', sm: 'table-cell' }}>
                        <HStack
                          as="span" display="inline-flex"
                          px="10px" py="4px" borderRadius="999px"
                          bg={status.active ? '#DCF1E6' : 'cream.bg2'}
                          color={status.active ? '#1E7A4E' : 'ink.400'}
                          fontSize="12px" fontWeight="700"
                        >
                          <Text>{status.active ? 'Activo' : 'Inactivo'}</Text>
                        </HStack>
                      </Td>
                      <Td py={3} isNumeric>
                        <HStack spacing="6px" justify="flex-end">
                          <Tooltip label="Editar">
                            <IconButton
                              icon={<FiEdit3 />} w="32px" h="32px" minW="32px" borderRadius="9px"
                              bg="cream.bg2" color="ink.400" border="none"
                              _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                              onClick={() => openEdit(status)} aria-label="Editar"
                            />
                          </Tooltip>
                          <Tooltip label="Eliminar">
                            <IconButton
                              icon={<FiTrash2 />} w="32px" h="32px" minW="32px" borderRadius="9px"
                              bg="cream.bg2" color="danger" border="none"
                              _hover={{ bg: '#FEE2E2' }}
                              onClick={() => confirmDelete(status)}
                              isLoading={deleting === status.id} aria-label="Eliminar"
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Modal crear / editar ── */}
      <Modal isOpen={isFormOpen} onClose={onCloseForm} isCentered size="md" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>{editing ? 'Editar estado' : 'Nuevo estado de alumno'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Alumno</FormLabel>
                <StudentPicker value={form.student} onSelect={(s) => setForm(f => ({ ...f, student: s }))} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Tipo de estado</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.type} onChange={setF('type')}>
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </Select>
              </FormControl>

              {form.type === 'sick_exit' ? (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Título</FormLabel>
                  <Select size="sm" borderRadius="8px" value={form.reason || SICK_EXIT_TITLES[1]} onChange={setF('reason')}>
                    {SICK_EXIT_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </FormControl>
              ) : (
                <FormControl>
                  <FormLabel fontSize="sm">Motivo</FormLabel>
                  <Input size="sm" borderRadius="8px" placeholder="Ej: Reposo médico"
                    value={form.reason} onChange={setF('reason')} />
                </FormControl>
              )}

              <FormControl>
                <FormLabel fontSize="sm">Notas internas</FormLabel>
                <Textarea size="sm" borderRadius="8px" rows={2}
                  value={form.notes} onChange={setF('notes')} />
              </FormControl>

              {form.type === 'sick_exit' ? (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Fecha</FormLabel>
                  <Input type="date" size="sm" borderRadius="8px"
                    value={form.date} onChange={setF('date')} />
                </FormControl>
              ) : (
                <HStack spacing={3} align="flex-start">
                  <FormControl>
                    <FormLabel fontSize="sm">Desde</FormLabel>
                    <Input type="datetime-local" size="sm" borderRadius="8px"
                      value={form.starts_at} onChange={setF('starts_at')} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Hasta</FormLabel>
                    <Input type="datetime-local" size="sm" borderRadius="8px"
                      value={form.ends_at} onChange={setF('ends_at')} />
                  </FormControl>
                </HStack>
              )}

              <FormControl>
                <FormLabel fontSize="sm">Autorizado por</FormLabel>
                <StaffPicker value={form.authorized_by}
                  onSelect={(p) => setForm(f => ({ ...f, authorized_by: p }))}
                  placeholder="Buscar quien autoriza…" />
              </FormControl>

              {/* Encargado — solo tiene sentido para Salida Pedagógica y
                  Salida por Enfermedad (quien acompaña físicamente al
                  alumno), no para suspensión/licencia. En Salida Pedagógica
                  es personal del colegio (StaffPicker); en Salida por
                  Enfermedad es quien retira al alumno — por defecto su
                  apoderado registrado, u otra persona (GuardianPicker, NO
                  busca en datofy-api). */}
              {form.type === 'pedagogical_exit' && (
                <FormControl>
                  <FormLabel fontSize="sm">Responsable (encargado)</FormLabel>
                  <StaffPicker value={form.responsible_name}
                    onSelect={(p) => setForm(f => ({ ...f, responsible_name: p }))}
                    placeholder="Buscar encargado…" />
                </FormControl>
              )}
              {form.type === 'sick_exit' && (
                <FormControl>
                  <FormLabel fontSize="sm">Responsable (quien retira)</FormLabel>
                  <GuardianPicker metaData={form.student?.meta_data} value={form.responsible_name}
                    onSelect={(p) => setForm(f => ({ ...f, responsible_name: p }))} />
                </FormControl>
              )}

              <Divider />

              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Activo</FormLabel>
                <Switch isChecked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  colorScheme="secondary" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseForm}>Cancelar</Button>
            <Button variant="educational-primary" size="sm" onClick={handleSave} isLoading={saving}>
              {editing ? 'Actualizar' : 'Crear estado'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Modal eliminar ── */}
      <Modal isOpen={isDeleteOpen} onClose={onCloseDelete} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={3}>
          <ModalHeader>Eliminar estado</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} spacing={3}>
              <Icon as={FiAlertTriangle} color="red.500" boxSize={6} />
              <Text fontWeight="semibold">¿Eliminar este estado?</Text>
            </HStack>
            <Text fontSize="sm" color={mutedColor}>
              Esta acción no se puede deshacer.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={onCloseDelete}>Cancelar</Button>
            <Button colorScheme="red" size="sm" onClick={handleDelete} isLoading={!!deleting}>
              Sí, eliminar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StudentStatusesTab;
